/** Synth-only audio for the prototype: river bed, paddles, squeaks, beeps. */
export class AudioSystem {
  private context: AudioContext | null = null;
  private unlocked = false;
  private riverGain: GainNode | null = null;
  private riverFilter: BiquadFilterNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  private bus: GainNode | null = null;
  private echoWet: GainNode | null = null;

  constructor() {
    // Browsers only let audio start from a real user gesture, and for touch
    // that's the END of a tap (pointerup/touchend), not pointerdown. Keep
    // trying on every gesture until the context is actually running.
    const events = ['pointerup', 'touchend', 'click', 'keydown'] as const;
    const unlock = () => {
      this.unlock();
      void this.context?.resume().then(() => {
        if (this.context?.state === 'running') for (const name of events) window.removeEventListener(name, unlock, true);
      });
    };
    for (const name of events) window.addEventListener(name, unlock, true);
  }

  unlock(): void {
    if (this.unlocked) return;
    const AudioContextClass =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) return;
    // iPhones mute web audio on the silent switch unless it's marked as playback (Safari 16.4+).
    const session = (navigator as unknown as { audioSession?: { type: string } }).audioSession;
    if (session) session.type = 'playback';
    const ctx = new AudioContextClass();
    this.context = ctx;
    void ctx.resume();
    this.unlocked = true;

    const length = ctx.sampleRate * 2;
    this.noiseBuffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const data = this.noiseBuffer.getChannelData(0);
    // Brown-ish noise: smoother, more like running water than white noise.
    let last = 0;
    for (let i = 0; i < length; i += 1) {
      last = (last + 0.02 * (Math.random() * 2 - 1)) / 1.02;
      data[i] = last * 3.5;
    }

    const source = ctx.createBufferSource();
    source.buffer = this.noiseBuffer;
    source.loop = true;
    this.riverFilter = ctx.createBiquadFilter();
    this.riverFilter.type = 'lowpass';
    this.riverFilter.frequency.value = 500;
    this.riverGain = ctx.createGain();
    this.riverGain.gain.value = 0;
    source.connect(this.riverFilter).connect(this.riverGain).connect(this.out(ctx));
    source.start();
  }

  /** Everything plays through one bus, which can feed an echo (for the pipe). */
  private out(ctx: AudioContext): AudioNode {
    if (!this.bus) {
      this.bus = ctx.createGain();
      this.bus.connect(ctx.destination);
      const delay = ctx.createDelay(1);
      delay.delayTime.value = 0.17;
      const feedback = ctx.createGain();
      feedback.gain.value = 0.38;
      this.echoWet = ctx.createGain();
      this.echoWet.gain.value = 0;
      this.bus.connect(delay);
      delay.connect(feedback).connect(delay);
      delay.connect(this.echoWet).connect(ctx.destination);
    }
    return this.bus;
  }

  /** 0..1 how much echo to add (inside the pipe). */
  setEcho(amount: number): void {
    const ctx = this.ready;
    if (!ctx || !this.echoWet) return;
    this.echoWet.gain.setTargetAtTime(amount * 0.55, ctx.currentTime, 0.15);
  }

  setPaused(paused: boolean): void {
    if (!this.context) return;
    if (paused) void this.context.suspend();
    else void this.context.resume();
  }

  /** Heron warning: a rising croak. */
  squawk(): void {
    const ctx = this.ready;
    if (!ctx) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(260, now);
    osc.frequency.exponentialRampToValueAtTime(520, now + 0.25);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.07, now + 0.03);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.3);
    osc.connect(gain).connect(this.out(ctx));
    osc.start(now);
    osc.stop(now + 0.32);
  }

  /** Heavy wooden clunk for the sluice gate. */
  clunk(open: boolean): void {
    const ctx = this.ready;
    if (!ctx) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'square';
    osc.frequency.setValueAtTime(open ? 140 : 110, now);
    osc.frequency.exponentialRampToValueAtTime(55, now + 0.15);
    gain.gain.setValueAtTime(0.07, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
    osc.connect(gain).connect(this.out(ctx));
    osc.start(now);
    osc.stop(now + 0.2);
  }

  /** 'running' once sound can play; for diagnostics. */
  get state(): string {
    return this.context?.state ?? 'not-started';
  }

  private get ready(): AudioContext | null {
    return this.context && this.context.state === 'running' ? this.context : null;
  }

  /** speed01: how fast the duck is travelling relative to the fastest water. */
  setRiver(speed01: number): void {
    const ctx = this.ready;
    if (!ctx || !this.riverGain || !this.riverFilter) return;
    const now = ctx.currentTime;
    this.riverGain.gain.setTargetAtTime(0.1 + speed01 * 0.22, now, 0.2);
    this.riverFilter.frequency.setTargetAtTime(380 + speed01 * 1400, now, 0.2);
  }

  paddle(): void {
    const ctx = this.ready;
    if (!ctx || !this.noiseBuffer) return;
    const now = ctx.currentTime;
    const source = ctx.createBufferSource();
    source.buffer = this.noiseBuffer;
    source.playbackRate.value = 2.5 + Math.random() * 0.8;
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 1400 + Math.random() * 500;
    filter.Q.value = 1.2;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.18, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.14);
    source.connect(filter).connect(gain).connect(this.out(ctx));
    source.start(now, Math.random());
    source.stop(now + 0.16);
  }

  /** A paddle stroke; `surf` 0..1 is how fast the water was (bigger, brighter splash). */
  stroke(surf: number): void {
    const ctx = this.ready;
    if (!ctx || !this.noiseBuffer) return;
    const now = ctx.currentTime;
    const source = ctx.createBufferSource();
    source.buffer = this.noiseBuffer;
    source.playbackRate.value = 1.6 + surf;
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(700 + surf * 600, now);
    filter.frequency.exponentialRampToValueAtTime(2600 + surf * 1800, now + 0.18);
    filter.Q.value = 0.9;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.35 + surf * 0.2, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.3);
    source.connect(filter).connect(gain).connect(this.out(ctx));
    source.start(now, Math.random());
    source.stop(now + 0.32);
  }

  /** Rising whoosh when the duck catches fast water. */
  whoosh(): void {
    const ctx = this.ready;
    if (!ctx || !this.noiseBuffer) return;
    const now = ctx.currentTime;
    const source = ctx.createBufferSource();
    source.buffer = this.noiseBuffer;
    source.playbackRate.value = 2;
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.Q.value = 2;
    filter.frequency.setValueAtTime(500, now);
    filter.frequency.exponentialRampToValueAtTime(3000, now + 0.35);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.3, now + 0.15);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.45);
    source.connect(filter).connect(gain).connect(this.out(ctx));
    source.start(now, Math.random());
    source.stop(now + 0.5);
  }

  /** Bright two-note chime for a close shave. */
  chime(): void {
    const ctx = this.ready;
    if (!ctx) return;
    [1318, 1760].forEach((freq, i) => {
      const now = ctx.currentTime + i * 0.07;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.12, now + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.3);
      osc.connect(gain).connect(this.out(ctx));
      osc.start(now);
      osc.stop(now + 0.32);
    });
  }

  /** Crumb pickup; the pitch climbs with the streak so chains feel good. */
  crumb(streak: number): void {
    const ctx = this.ready;
    if (!ctx) return;
    const now = ctx.currentTime;
    const step = Math.min(streak - 1, 12);
    const freq = 660 * Math.pow(2, step / 12);
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(freq, now);
    osc.frequency.exponentialRampToValueAtTime(freq * 1.5, now + 0.05);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.08, now + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
    osc.connect(gain).connect(this.out(ctx));
    osc.start(now);
    osc.stop(now + 0.14);
  }

  /** Out of paddle charges. */
  dud(): void {
    const ctx = this.ready;
    if (!ctx) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(220, now);
    osc.frequency.exponentialRampToValueAtTime(150, now + 0.1);
    gain.gain.setValueAtTime(0.06, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
    osc.connect(gain).connect(this.out(ctx));
    osc.start(now);
    osc.stop(now + 0.14);
  }

  squeak(strength: number): void {
    const ctx = this.ready;
    if (!ctx) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const base = 900 + Math.random() * 200;
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(base, now);
    osc.frequency.exponentialRampToValueAtTime(base * 1.6, now + 0.06);
    osc.frequency.exponentialRampToValueAtTime(base * 0.8, now + 0.18);
    const volume = Math.min(0.22, 0.07 + strength * 0.04);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(volume, now + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
    osc.connect(gain).connect(this.out(ctx));
    osc.start(now);
    osc.stop(now + 0.24);
    this.thud(0.12 + strength * 0.03);
  }

  thud(volume = 0.1): void {
    const ctx = this.ready;
    if (!ctx) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(160, now);
    osc.frequency.exponentialRampToValueAtTime(60, now + 0.12);
    gain.gain.setValueAtTime(Math.min(0.3, volume), now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.16);
    osc.connect(gain).connect(this.out(ctx));
    osc.start(now);
    osc.stop(now + 0.18);
  }

  beep(high: boolean): void {
    const ctx = this.ready;
    if (!ctx) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'square';
    osc.frequency.value = high ? 1046 : 523;
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.06, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + (high ? 0.4 : 0.16));
    osc.connect(gain).connect(this.out(ctx));
    osc.start(now);
    osc.stop(now + 0.42);
  }

  finish(isBest: boolean): void {
    const ctx = this.ready;
    if (!ctx) return;
    const notes = isBest ? [523, 659, 784, 1046, 1318] : [523, 659, 784];
    notes.forEach((freq, i) => {
      const now = ctx.currentTime + i * 0.09;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.12, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);
      osc.connect(gain).connect(this.out(ctx));
      osc.start(now);
      osc.stop(now + 0.4);
    });
  }

  dispose(): void {
    void this.context?.close();
    this.context = null;
  }
}
